/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Fragment, useState, useEffect, useRef, useContext } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import ReactPlayer from 'react-player';
import { analytics } from "../../helpers/analytics";

import { client } from '../../helpers/contentful';
import { Card } from '../Card';
import { Dropdown } from '../Dropdown';
import { PlayIcon } from '../../Icons';
import { ToolsListItem } from '../ToolsModal/ToolsListItem';
import { UserContext } from '../../context/userContext';
import { PaywallModal } from '../PaywallModal';
import { IBrushFields, ICourseFields } from '../../@types/generated/contentful';
import { SignIn } from '../SignIn';
import { getContentfulImageSrcSet, getContentfulImageUrl } from '../../helpers/image';

type Props = {
  course: ICourseFields;
  isOpen: boolean;
  onClose: () => void;
};

type SelectedLesson = {
  id: number;
  title?: string;
};

const CourseModal = ({ isOpen, course, onClose }: Props) => {
  const [selectedLesson, setSelectedLesson] = useState<SelectedLesson>();
  const [brushes, setBrushes] = useState<IBrushFields[]>([]);
  const [play, playVideo] = useState(false);
  const [isPaywallOpen, setPaywallOpen] = useState(false);
  const [isAuthOpen, setAuthOpen] = useState(false);
  const { illustration, colors, fontColor, title, subtitle, numberOfHours, numberOfSection, brushSets } = course;
  const illustrationImage = getContentfulImageUrl(illustration, { width: 760 });
  const illustrationSrcSet = getContentfulImageSrcSet(illustration, [420, 760, 1100]);

  const player = useRef<null | ReactPlayer>(null);
  const progressMilestone = useRef(0);

  const dropdownOptions =
    course.section &&
    course.section.map((item, i: number) => ({
      id: i,
      title: item.fields.title,
    }));

  const handleClose = () => {
    analytics.track("Course Modal Closed", {
      course: title,
      lesson: selectedLesson?.title,
    });
    onClose();
    playVideo(false);
  };

  useEffect(() => {
    setSelectedLesson({
      id: 0,
      title: course.section && course.section[0].fields.title ? course.section[0].fields.title : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getBrushes = async () => {
      const response = await client
        .getEntries<IBrushFields>({
          'fields.categoryName': 'Brushes',
          content_type: 'brush',
          'fields.title[in]': brushSets.join(),
        })
        .then((r) => r.items);

      return response;
    };

    getBrushes().then((r) => {
      const fields = r.map((brush) => brush.fields);
      setBrushes(fields);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTime = (time: any) => {
    if (selectedLesson && time.playedSeconds !== 0) {
      localStorage.setItem(`${title}_${selectedLesson.title}`, time.playedSeconds);
    }

    const playedPercent = Math.floor((time.played || 0) * 100);
    const nextMilestone = [25, 50, 75, 90].find(
      (milestone) =>
        playedPercent >= milestone && progressMilestone.current < milestone
    );

    if (nextMilestone && selectedLesson) {
      progressMilestone.current = nextMilestone;
      analytics.track("Course Video Progress", {
        course: title,
        lesson: selectedLesson.title,
        progress_percent: nextMilestone,
        played_seconds: Math.round(time.playedSeconds || 0),
      });
    }
  };

  const setPlayerStartTime = () => {
    if (player && player.current && selectedLesson) {
      const time = localStorage.getItem(`${title}_${selectedLesson.title}`);
      player.current.seekTo(time ? Number(time) : 0, 'seconds');
    }
  };

  const { user } = useContext(UserContext);
  const { hasPremium = false, plan = {} } = user || {};

  const handleDownloadButtonClick = () => {
    if (user && user.email) {
      setPaywallOpen(true);
      analytics.track('Paywall', {
        premium: hasPremium,
        trial: plan?.isTrial || false,
        source: "course_download",
        course: title,
      });
    } else {
      analytics.track("Auth Required", {
        source: "course_download",
        course: title,
      });
      setAuthOpen(true);
    }
  };

  const handleAuthClose = () => {
    setAuthOpen(false);
  };

  const handleAuthSuccess = () => {
    setAuthOpen(false);
    analytics.track("Auth Gate Completed", {
      source: "course_download",
      course: title,
    });
    setPaywallOpen(true);
  };

  const handleDropdownChange = (option: SelectedLesson) => {
    setSelectedLesson(option);
    progressMilestone.current = 0;
    analytics.track('Academy Course Selected', {
      academySelected: title,
      courseSelected: option.title,
      premium: hasPremium,
    });
    analytics.track("Course Lesson Selected", {
      course: title,
      lesson: option.title,
      lesson_index: option.id,
      premium: hasPremium,
    });
  };

  const handlePlayClick = () => {
    playVideo(true);
    analytics.track("Course Video Played", {
      course: title,
      lesson: selectedLesson?.title,
      lesson_index: selectedLesson?.id,
      premium: hasPremium,
    });
  };

  useEffect(() => {
    if (isOpen) {
      analytics.track("Course Modal Viewed", {
        course: title,
        section_count: numberOfSection,
        duration_minutes: numberOfHours,
        brush_sets_count: brushSets?.length,
      });
    }
  }, [brushSets?.length, isOpen, numberOfHours, numberOfSection, title]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='fixed z-10 inset-0 overflow-y-auto' onClose={handleClose}>
        <div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <span className='hidden sm:inline-block sm:align-middle sm:h-screen' aria-hidden='true'>
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            enterTo='opacity-100 translate-y-0 sm:scale-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100 translate-y-0 sm:scale-100'
            leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          >
            <div
              className={`
          relative 
        inline-block 
        overflow-hidden 
        transform 
        transition-all s
        align-middle 
        max-w-[683px]
        w-[82vw]
        md:w-full
        `}
            >
              <Card theme='light' size='large' onClose={handleClose}>
                <div className='relative'>
                  <div
                    className={`
                w-full
                bg-center
                bg-cover
                relative 
                rounded-t-[22px] md:rounded-none flex flex items-end overflow-visible`}
                  >
                    {course.section && (
                      <div className='relative pb-[56%] w-full '>
                        <ReactPlayer
                          url={course.section[selectedLesson ? selectedLesson.id : 0].fields.videoUrl}
                          controls
                          playing={play}
                          className='video rounded-t-[22px] absolute overflow-hidden'
                          onPause={() => playVideo(false)}
                          onProgress={(time) => setTime(time)}
                          ref={player}
                          onReady={() => setPlayerStartTime()}
                          style={{ opacity: play ? 1 : 0 }}
                        />
                      </div>
                    )}

                    {!play && (
                      <>
                        <div
                          className='absolute z-[1] w-full h-full flex items-end rounded-t-[22px] bottom-0'
                          style={{
                            backgroundImage: `linear-gradient(to top, ${colors[0]} 0%, ${colors[1]} 100%)`,
                            color: fontColor,
                          }}
                        >
                          <div
                            className={`w-[21%] 
                          text-white cursor-pointer 
                          absolute -translate-x-2/4 -translate-y-2/4 left-2/4 top-2/4 z-[1]`}
                            onClick={handlePlayClick}
                            style={{ outline: 'none' }}
                          >
                            <PlayIcon />
                          </div>

                          {illustrationImage && (
                            <img
                              src={illustrationImage}
                              srcSet={illustrationSrcSet}
                              sizes='(max-width: 768px) 100vw, 683px'
                              alt=''
                              loading='lazy'
                              decoding='async'
                              className={`
                          max-w-[100%] max-h-[80%] 
                          select-none absolute -translate-x-2/4 -translate-y-2/4 left-2/4 top-2/4`}
                            />
                          )}

                          <div
                            className='flex justify-between w-full items-end h-[60%] p-6 md:p-4'
                            style={{ color: fontColor }}
                          >
                            <div className='text-left z-[1] relative'>
                              {selectedLesson && (
                                <Dropdown
                                  options={dropdownOptions}
                                  setSelected={handleDropdownChange}
                                  selected={selectedLesson}
                                />
                              )}
                              <h1 className='text-3xl leading-3xl font-bold mt-4 sm:text-2xl sm:leading-2xl sm:mt-2'>
                                {title}
                              </h1>
                              <p className='text-sm leading-sm sm:text-xxs sm:leading-xxs'>
                                {numberOfSection} sections — {numberOfHours} minutes
                              </p>
                              <p className='text-lg leading-lg sm:text-sm sm:leading-sm'>{subtitle}</p>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  {brushes.length > 0 &&
                    brushes.map((item) => {
                      if (item.section) {
                        return (
                          <ToolsListItem
                            key={item.section[0].fields.title}
                            brush={item.section[0]}
                            setTitle={item.section[0].fields.title}
                            image={item.image}
                            onDownload={handleDownloadButtonClick}
                          />
                        );
                      }
                      return <></>;
                    })}
                  <div className='px-8 py-6 md:px-6 md:py-4'>
                    <p className='text-left text-base leading-base sm:text-sm sm:leading-sm'>
                      {course.section && course.section[selectedLesson ? selectedLesson.id : 0].fields.content}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </Transition.Child>
        </div>
        <PaywallModal
          isOpen={isPaywallOpen}
          onClose={() => setPaywallOpen(false)}
          userId={(user && user.id) || null}
          email={(user && user.email) || null}
          hasPremium={hasPremium}
          isTrial={plan?.isTrial || false}
        />
        <SignIn isOpen={isAuthOpen} onClose={handleAuthClose} onSuccess={handleAuthSuccess} />
      </Dialog>
    </Transition.Root>
  );
};

export { CourseModal };
